import React, { useEffect, useState } from 'react';
import {
  Activity,
  BookOpen,
  Building2,
  Coins,
  Hash,
  Landmark,
  RefreshCw,
  Scale,
  Search,
  TrendingDown,
  TrendingUp,
  Wallet
} from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailField,
  DetailFieldList,
  DetailIdentity
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { Skeleton } from '../../../../../components/shared/Skeleton/Skeleton';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { useToast } from '../../../../../hooks/useToast';
import {
  BankAccountRecord,
  BankMovement,
  listBankAccounts,
  listBankMovements
} from '../../../../../services/BankAccountsService';
import '../../shared-modules.css';
import './FinanceMasters.css';

// Cuenta PGC de bancos: la API no devuelve la cuenta contable de cada cuenta bancaria
const BANK_PGC_ACCOUNT = '572000';

const formatMoney = (val: number, currency: string, privacyMode: boolean) => {
  if (privacyMode) return '•••••';
  const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;
  return `${val.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
};

interface FinanceBankAccountsViewProps {
  privacyMode: boolean;
}

export const FinanceBankAccountsView = ({ privacyMode }: FinanceBankAccountsViewProps) => {
  const { notify } = useToast();

  const [accounts, setAccounts] = useState<BankAccountRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedAccount, setSelectedAccount] = useState<BankAccountRecord | null>(null);
  const [movements, setMovements] = useState<BankMovement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const list = await listBankAccounts();
      setAccounts(list);
      setListError('');
      setSelectedAccount(prev => (prev ? list.find(a => a.id === prev.id) || null : null));
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
      setAccounts([]);
      setSelectedAccount(null);
      setListError(err instanceof Error ? err.message : 'No se pudieron cargar las cuentas bancarias.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (!selectedAccount) return;
    let cancelled = false;
    setMovementsLoading(true);
    listBankMovements(selectedAccount.id)
      .then(list => { if (!cancelled) setMovements(list); })
      .catch(err => {
        console.error('Error fetching bank movements:', err);
        if (!cancelled) setMovements([]);
      })
      .finally(() => { if (!cancelled) setMovementsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedAccount?.id]);

  const handleSync = async () => {
    await fetchAccounts();
    notify('Sincronización bancaria', 'Cuentas bancarias actualizadas.', 'info');
  };

  const closePanel = () => setSelectedAccount(null);

  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedAccount));

  const isSynced = (a: BankAccountRecord) => a.status === 'active';

  // La API solo devuelve un saldo: el contable coincide con el del banco hasta que haya conciliación
  const accountingBalance = (a: BankAccountRecord) => a.balance;

  const query = searchQuery.toLowerCase();
  const filteredAccounts = accounts.filter(a =>
    a.name.toLowerCase().includes(query) ||
    a.currency.toLowerCase().includes(query) ||
    a.id.toLowerCase().includes(query)
  );

  const columns: ColumnDef<BankAccountRecord>[] = [
    {
      id: 'name',
      header: 'CUENTA',
      render: (a) => (
        <>
          <div className="fin-master__name">{a.name}</div>
          <div className="fin-master__sub">Cuenta PGC: {BANK_PGC_ACCOUNT}</div>
        </>
      )
    },
    {
      id: 'currency',
      header: 'MONEDA',
      align: 'center',
      render: (a) => <span className="fin-master__chip">{a.currency}</span>
    },
    {
      id: 'balance',
      header: 'SALDO EN BANCO',
      align: 'right',
      render: (a) => <span className="fin-master__amount">{formatMoney(a.balance, a.currency, privacyMode)}</span>
    },
    {
      id: 'accountingBalance',
      header: 'SALDO EN CONTABILIDAD',
      align: 'right',
      render: (a) => <span className="fin-master__amount">{formatMoney(accountingBalance(a), a.currency, privacyMode)}</span>
    },
    {
      id: 'gap',
      header: 'DESFASE',
      align: 'right',
      render: (a) => {
        const gap = a.balance - accountingBalance(a);
        return (
          <span className={`fin-master__amount ${Math.abs(gap) > 0.01 ? 'pending' : ''}`}>
            {formatMoney(gap, a.currency, privacyMode)}
          </span>
        );
      }
    },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (a) => (
        <span className={`module-badge ${isSynced(a) ? 'active' : 'draft'}`}>{isSynced(a) ? 'Sincronizado' : 'Revisar'}</span>
      )
    }
  ];

  const renderMovementsSkeleton = () => (
    <div className="fin-master__attachments">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="fin-master__attachment">
          <div className="skeleton-block skeleton-block--tight" style={{ flex: 1 }}>
            <Skeleton width="60%" height="10px" />
            <Skeleton width="30%" height="8px" />
          </div>
          <Skeleton width="60px" height="10px" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR NOMBRE, MONEDA O ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button
          onClick={handleSync}
          className="module-create-btn"
          disabled
          title="Sincronización bancaria desactivada"
          data-keep-detail
        >
          <RefreshCw size={14} /> Sincronizar_Cuentas
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedAccount)}>
        {listError ? (
          <div className="module-panel">
            <div className="module-panel-empty">{listError}</div>
          </div>
        ) : (
          <DataTable
            data={filteredAccounts}
            columns={columns}
            title={<><Landmark size={14} className="module-panel-title-icon" /> Cuentas_Bancarias_Y_Tesorería</>}
            subtitle={loading ? undefined : `TOTAL: ${filteredAccounts.length}`}
            isLoading={loading}
            getRowId={(a) => a.id}
            onRowClick={(a) => setSelectedAccount(a)}
            selectedRowId={selectedAccount?.id}
            emptyText="No se han encontrado cuentas bancarias registradas."
            actionsHeader="ACCIONES"
            actionsColumn={(a) => (
              <RowActionsMenu actions={[viewAction(() => setSelectedAccount(a))]} />
            )}
          />
        )}

        {selectedAccount ? (
          <DetailPanel
            expandable
            title="Ficha_De_Cuenta_Bancaria"
            onClose={closePanel}
            cards={
              <DetailCard
                icon={Activity}
                title={`Últimos_Movimientos ${movementsLoading ? '' : `(${movements.length})`}`}
              >
                {movementsLoading ? (
                  renderMovementsSkeleton()
                ) : movements.length === 0 ? (
                  <div className="fin-master__empty">Sin movimientos registrados</div>
                ) : (
                  <div className="fin-master__attachments">
                    {movements.slice(0, 10).map(m => (
                      <div key={m.id} className="fin-master__attachment">
                        <div>
                          <div className="fin-master__text">{m.description}</div>
                          <div className="fin-master__sub">{m.date || '—'}</div>
                        </div>
                        <span className={`fin-master__movement ${m.type}`}>
                          {m.type === 'income' ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                          {formatMoney(m.amount, selectedAccount.currency, privacyMode)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </DetailCard>
            }
          >
            <DetailIdentity
              icon={Building2}
              reference={`ID ${selectedAccount.id}`}
              name={selectedAccount.name}
              subtitle={
                <span className={`module-badge ${isSynced(selectedAccount) ? 'active' : 'draft'}`}>
                  {isSynced(selectedAccount) ? 'Sincronizado' : 'Revisar'}
                </span>
              }
            />

            <DetailFieldList>
              <DetailField icon={BookOpen} label="CUENTA_PGC">{BANK_PGC_ACCOUNT}</DetailField>
              <DetailField icon={Coins} label="MONEDA">{selectedAccount.currency}</DetailField>
              <DetailField icon={Wallet} label="SALDO_EN_BANCO">
                {formatMoney(selectedAccount.balance, selectedAccount.currency, privacyMode)}
              </DetailField>
              <DetailField icon={Hash} label="SALDO_EN_CONTABILIDAD">
                {formatMoney(accountingBalance(selectedAccount), selectedAccount.currency, privacyMode)}
              </DetailField>
              <DetailField icon={Scale} label="DESFASE_DE_CONCILIACIÓN">
                {formatMoney(selectedAccount.balance - accountingBalance(selectedAccount), selectedAccount.currency, privacyMode)}
              </DetailField>
            </DetailFieldList>
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>
    </div>
  );
};

export default FinanceBankAccountsView;
